import { z } from 'zod';
import { MAX_PLAYERS, ROOM_CODE_LENGTH, TIMERS } from './config.js';
import { ABSTAIN } from './state.js';
import { GAME_IDS, ONE_NIGHT_CENTRE_CARDS } from './games.js';
import { WHISPER_EMOTES } from './protocol.js';
/**
 * Runtime validation for inbound ClientIntents. The server parses every message
 * through `parseClientIntent` before touching game state — untrusted input never
 * reaches the engine unvalidated.
 */
const name = z.string().trim().min(1, 'name required').max(24, 'name too long');
const code = z
    .string()
    .trim()
    .toUpperCase()
    .length(ROOM_CODE_LENGTH, `code must be ${ROOM_CODE_LENGTH} letters`);
const sessionToken = z.string().min(1).max(200);
const playerId = z.string().min(1).max(200);
/**
 * DERIVED from `GAME_IDS` — never hand-list game ids here. Both the create and
 * switch schemas used to carry their own literal enums, so adding a game made
 * the server silently reject CREATE_ROOM/SWITCH_GAME for it until all three
 * lists were updated. One source of truth now.
 */
const gameId = z.enum(GAME_IDS);
export const createRoomSchema = z.object({
    type: z.literal('CREATE_ROOM'),
    name,
    gameId: gameId.optional(),
});
export const joinRoomSchema = z.object({
    type: z.literal('JOIN_ROOM'),
    code,
    name,
});
export const rejoinSchema = z.object({
    type: z.literal('REJOIN'),
    code,
    sessionToken,
});
export const setConfigSchema = z.object({
    type: z.literal('SET_CONFIG'),
    discussionSecs: z.number().int().min(TIMERS.DISCUSS_MIN).max(TIMERS.DISCUSS_MAX),
});
export const startGameSchema = z.object({
    type: z.literal('START_GAME'),
});
export const readySchema = z.object({
    type: z.literal('READY'),
});
export const submitNightTargetSchema = z.object({
    type: z.literal('SUBMIT_NIGHT_TARGET'),
    targetId: playerId,
});
export const submitInvestigateSchema = z.object({
    type: z.literal('SUBMIT_INVESTIGATE'),
    targetId: playerId.nullable(),
});
export const submitVoteSchema = z.object({
    type: z.literal('SUBMIT_VOTE'),
    targetId: z.union([playerId, z.literal(ABSTAIN), z.null()]),
});
export const hostAdvanceSchema = z.object({
    type: z.literal('HOST_ADVANCE'),
});
export const hostSetPausedSchema = z.object({
    type: z.literal('HOST_SET_PAUSED'),
    paused: z.boolean(),
});
export const playAgainSchema = z.object({
    type: z.literal('PLAY_AGAIN'),
});
export const switchGameSchema = z.object({
    type: z.literal('SWITCH_GAME'),
    gameId,
});
export const claimStageSchema = z.object({
    type: z.literal('CLAIM_STAGE'),
});
export const watchSchema = z.object({
    type: z.literal('WATCH'),
    code,
});
export const setNarratorOnlySchema = z.object({
    type: z.literal('SET_NARRATOR_ONLY'),
    narratorOnly: z.boolean(),
});
export const kickPlayerSchema = z.object({
    type: z.literal('KICK_PLAYER'),
    playerId,
});
/**
 * Host-only, LOBBY-only, Blood-Moon-only (all three gates are server-side).
 * `count` is bounded by the biggest table any game seats — the server clamps
 * again to the seats actually free, so this only stops an unbounded number
 * reaching the roster loop.
 */
export const addAiSeatSchema = z.object({
    type: z.literal('ADD_AI_SEAT'),
    count: z.number().int().min(1).max(MAX_PLAYERS).optional(),
});
/**
 * Host-only, LOBBY/END only (both gates are server-side — the intent carries no
 * payload to validate, so zod only pins the shape).
 */
export const resetScoresSchema = z.object({
    type: z.literal('RESET_SCORES'),
});
// ── The Whisper intents ───────────────────────────────────────────────────────
/** Trim, require non-empty, cap at 30 chars (spec §5 — blocks spam-walls). */
export const submitWhisperSchema = z.object({
    type: z.literal('SUBMIT_WHISPER'),
    text: z.string().trim().min(1, 'say something').max(30, 'keep it under 30 characters'),
});
export const paladinHoldSchema = z.object({
    type: z.literal('PALADIN_HOLD'),
    targetId: playerId,
});
export const emoteSchema = z.object({
    type: z.literal('EMOTE'),
    emote: z.enum(WHISPER_EMOTES),
});
// ── Blood Moon: One Night intents ─────────────────────────────────────────────
// Shape-only validation. WHICH peek/swap a given role may legally make is the
// engine's call (it alone knows the phase and the secret dealt roles) — zod just
// guarantees the fields are well-formed and bounded so nothing unbounded reaches
// the reducer. At most 2 centre slots are ever readable (the Seer's two-card
// look), and slots are indices into a 3-card centre.
export const oneNightPeekSchema = z.object({
    type: z.literal('ONENIGHT_PEEK'),
    targetId: playerId.nullable(),
    centre: z.array(z.number().int().min(0).max(ONE_NIGHT_CENTRE_CARDS - 1)).max(2),
});
export const oneNightSwapSchema = z.object({
    type: z.literal('ONENIGHT_SWAP'),
    aId: playerId,
    bId: playerId,
});
/** Discriminated union of every intent — the full inbound protocol. */
export const clientIntentSchema = z.discriminatedUnion('type', [
    createRoomSchema,
    joinRoomSchema,
    rejoinSchema,
    setConfigSchema,
    startGameSchema,
    readySchema,
    submitNightTargetSchema,
    submitInvestigateSchema,
    submitVoteSchema,
    hostAdvanceSchema,
    hostSetPausedSchema,
    playAgainSchema,
    switchGameSchema,
    claimStageSchema,
    watchSchema,
    setNarratorOnlySchema,
    kickPlayerSchema,
    addAiSeatSchema,
    resetScoresSchema,
    // The Whisper
    submitWhisperSchema,
    paladinHoldSchema,
    emoteSchema,
    // Blood Moon: One Night
    oneNightPeekSchema,
    oneNightSwapSchema,
]);
/**
 * Safe-parse an unknown wire value into a ClientIntent.
 * Never throws — returns a discriminated result the server can branch on.
 */
export function parseClientIntent(input) {
    const parsed = clientIntentSchema.safeParse(input);
    if (parsed.success) {
        return { ok: true, intent: parsed.data };
    }
    const first = parsed.error.errors[0];
    const message = first ? `${first.path.join('.')}: ${first.message}` : 'invalid intent';
    return { ok: false, error: message };
}
/** Max players guard reused by the server when adding lobby members. */
export const maxPlayers = MAX_PLAYERS;
//# sourceMappingURL=zod.js.map